import { ReactNode, useState, useEffect, ReactElement } from 'react';
import { DndBarNode } from '../../dndBar';
import { registerNode, AdditionalNode, AdditionalComponentProps, ValidateClaimFn } from '.././additionalNodes';
import {guidGenerator} from '../../../../utils/utils';
import { JE_JsonValidationContext } from '../../../../jsonParsing/interfaces';
import {FQSchemaData} from '../../../../jsonParsing/fqSpecGeneration';
import useComponentUIHook from '../../componentUI/useComponentUIHook';
import TemplatizedFieldComponent, {getTemplatizedValue, TemplatizedValue} from '../../componentUI/templatizedFieldComponent'; 
import SessionManagement, {sessionFieldsAreValid} from '../../componentUI/sessionManagement';
import { jsonEditorStyles } from '../../jsonEditorStyles';
import { SelectComponent } from '../../../uiComponents';
import { Typography, SelectChangeEvent, Button, FormControl } from '@mui/material';
import { OptionProps } from '../../../../view-models';
import { cloneDeep } from 'lodash';
import {BuildSettings} from '../../../../jsonParsing/utils';
import { 
  createFQClaimXform, JE_FQClaimXformSpec,
  JE_FQClaimSpec, createFQClaim,
  createFQDisplayControl,
  createFQTechnicalProfile,
} from '../../../../jsonParsing/jsonFQContext';
import { addFQOrchestrationStepOrValidationProfileRef } from '../../../../jsonParsing/fqSpecGeneration';
import constants from '../../../../constants/jsonXMLConstants';


interface AuthenticatorAvailabilityData {
  availabilityClaim: string;
  idClaim: string;
  contentDefinition: any; 
}

function EditNode({data, closeGritComponent, setUnsavedChanges}: AdditionalComponentProps) {
  const classes = jsonEditorStyles();
  
  const [disableSubmit, setDisableSubmit] = useState(true);
  const {
    modifiedData,
    setModifiedData,
    allClaims,
    checkIfSameIgnoringSubFlows,
    modifiedTemplatizations,
    setModifiedTemplatizations
  } = useComponentUIHook(data);
  
  useEffect(() => {
    try {
      const _res: boolean = checkIfSameIgnoringSubFlows();
      setDisableSubmit(
        _res ||
          (!(
            modifiedData.availabilityClaim && modifiedData.idClaim
          )) ||
          (!sessionFieldsAreValid(modifiedData))
      );
      setUnsavedChanges(_res);
    } catch( error ) {
      console.log(error)
    }
  }, [modifiedData]);

  return <div className={classes.flexCol}>
      <Typography variant="body1" sx={{ pt: 2 }}>
          Warning: this node will cause an error if used after user signs up for the first time.
          If user signs up, there is no need to check availability; the user should be assumed to 
          not have any authenticator devices registered.
          </Typography>
      <Typography variant="body1" sx={{ pt: 1 }}>
      Select Claim that contains userPrincipalName
      </Typography>
      <div className={classes.flexRow} style={{ alignItems: 'center' }}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setModifiedData({
              ...modifiedData,
              idClaim: e.target.value,
            });
          }}
          data={{
            value: modifiedData.idClaim ? modifiedData.idClaim : '',
            label: 'ID Claim',
            required: true,
            style: { margin: 1, width: 200 },
            options: Object.keys(allClaims)
              .filter((c) => {
                return allClaims[c]?.dataType === 'string';
              })
              .reduce((acc: OptionProps[], curVal: string) => {
                return acc.concat({ label: curVal, value: curVal });
              }, [])
              .sort((a: OptionProps, b: OptionProps) => {
                return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
              }),
          }}
        />
      </div>
      <Typography variant="body1" sx={{ pt: 1 }}>
        Select boolean claim to indicate if user has Authenticator MFA available:
      </Typography>
      <div className={classes.flexRow} style={{ alignItems: 'center' }}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setModifiedData({
              ...modifiedData,
              availabilityClaim: e.target.value,
            });
          }}
          data={{
            value: modifiedData.availabilityClaim ? modifiedData.availabilityClaim : '',
            label: 'Availability Claim',
            required: true,
            style: { margin: 1, width: 200 },
            options: Object.keys(allClaims)
              .filter((c) => {
                return allClaims[c]?.dataType === 'boolean';
              })
              .reduce((acc: OptionProps[], curVal: string) => {
                return acc.concat({ label: curVal, value: curVal });
              }, [])
              .sort((a: OptionProps, b: OptionProps) => {
                return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
              }),
          }}
        />
      </div>
    <Button
      type="submit"
      fullWidth
      variant="contained"
      disabled={disableSubmit}
      sx={{ mt: 3, mb: 2 }}
      onClick={() => {
        closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
      }}
    >
      Save
    </Button>
  </div>
}


class AuthenticatorAvailability extends AdditionalNode {
  componentType = "Authenticator-Availability";
  label = "Authenticator Availability";
  getDndComponent() {
    return <DndBarNode componentType={this.componentType} label={this.label} />
  }
  
  getJSONCompDef() {
    return {
      nodeType: 'process',
      nodeSubType: 'default',
      label: this.label,
      idClaim: "",
      availabilityClaim:"",
      contentDefinition: {
        displayName: 'Content Definition',
      }
    }
  }
  
  initNodeJSON() {
    return {
      id: `${this.componentType}-` + guidGenerator(),
      componentType: this.componentType,
      availabilityClaim: "",
      idClaim: "",
      sessionManagement: {
        option: 'alwaysExecute',
        sessionId: ''
      }
    }
  }
  
  EditNodeUI(props: AdditionalComponentProps){ return <EditNode {...props} /> }
  
  validateJSON(
  nodeData: any,
  vCtx: JE_JsonValidationContext,
  addErrorReport: (message: string) => void,
  validateClaim: ValidateClaimFn
  ){
    const illegalParentNodes = ["aadCreate"] 
    const validateFormSubmit = (formId: string, vCtx: JE_JsonValidationContext) => {
      const rootNode = vCtx.flowcharts[formId].rootNodeId;
      validateTree(
        [rootNode], 
        vCtx, 
        (nodeId: string) => vCtx.nodes[nodeId].children
      )
    }
    const validateTree = (
      rootNodes: string[],
      vCtx: JE_JsonValidationContext,
      decendantFn: (id: string) => string[]
      ) => {
      rootNodes.forEach(nodeId => {
        const node = vCtx.nodes[nodeId];
        const parentType = node.nodeData.componentType;
        if(
          node.nodeData.formSubmitAction &&
          node.nodeData.formSubmitAction.id
        ) { 
          validateFormSubmit(node.nodeData.formSubmitAction.id, vCtx)
        }
        if(illegalParentNodes.includes(parentType))
          addErrorReport(
            `Authenticator Availability node cannot be used on the same branch as an AAD-Create node!`
          )
        validateTree(
          decendantFn(nodeId),
          vCtx,
          decendantFn
        )

      })
    }
    validateClaim(
      nodeData.nodeData.idClaim,
      "ID Claim",
      true
    )
    if(!nodeData.nodeData.availabilityClaim)
      addErrorReport("Availability Claim is not selected!")
      
    validateTree(
      nodeData.parents,
      vCtx,
      (nodeId: string) => vCtx.nodes[nodeId].parents
    )
  }
  
  generateFQSchema({node, nodes, journeyId, parsingCtx, branchVarMap, preconditions}: FQSchemaData) {
    //@ts-ignore
    const data:AuthenticatorAvailabilityData  = node.data;
    const deviceCount = createFQClaim(parsingCtx.claimStore, {
      id: "numberOfAvailableDevices",
      dataType: "int",
      displayName: "Number of devices that support MFA"
    });
    const setAvailabilityClaim = createFQClaimXform(parsingCtx.claimTransformStore, {
      id: "isNumDevicesGreaterThanZero",
      method: "AssertNumber",
      inputParameters:[{
        id:"Operator", dataType:"string", value:"GreaterThan"
      }, {
        id: "CompareToValue", dataType: "int", value: "0"
      }, { 
        id: "throwError", dataType:"boolean", value: "false"
      }],
      inputClaims: [{claimId: deviceCount.id, xformClaimType:"inputClaim" }],
      outputClaims: [{claimId: data.availabilityClaim, xformClaimType: "outputClaim" }]
    });
    const setDefaultValue = createFQTechnicalProfile(parsingCtx.technicalProfileStore, {
      id: "Set-TOTP-Defaults",
      protocol: constants.customPolicySchema.protocols.claimsTransformation,
      displayName: "Set TOTP default values",
      inputClaims: [{ claimId: data.idClaim }],
      outputClaims: [{claimId: deviceCount.id, defaultValue: '0'}],
    }, constants.claimsProviderNames.totp);
    const checkAvailabilityTP = createFQTechnicalProfile(parsingCtx.technicalProfileStore, {
      displayName: "Check availability of Authenticator MFA",
      id: node.sanitizedDisplayName,
      protocol: constants.customPolicySchema.protocols.azureMfa,
      metadata: [{key: "Operation", value: "GetAvailableDevices"}],
      inputClaims: [{
        claimId: data.idClaim, 
        partnerClaimType: "userPrincipalName",
      }],
      outputClaims: [{
          claimId: deviceCount.id
        },{
          claimId: data.availabilityClaim
      }],
      outputClaimXformIds: [setAvailabilityClaim.id]
    }, 'TOTP');
    
    const orchStepDefault = addFQOrchestrationStepOrValidationProfileRef(
      parsingCtx,
      node,
      nodes,
      setDefaultValue.id,
      "TOTP-Set-Default-Values",
      journeyId,
      branchVarMap,
      preconditions,
    );
    const orchStep = addFQOrchestrationStepOrValidationProfileRef(
      parsingCtx,
      node,
      nodes,
      checkAvailabilityTP.id,
      "TOTP-CheckAvailability",
      journeyId,
      branchVarMap,
      preconditions,
    );
  }
  
  getOutputClaims(nodeData: any, vCtx: JE_JsonValidationContext): string[] {
     return [ nodeData.idClaim, nodeData.availabilityClaim] 
  }
  
  extractFCNData(
    nodeData: any,
    componentType: string,
    buildSettings: BuildSettings,
    linkType?: string
  ){
    const idClaim = nodeData.idClaim;
    const availabilityClaim = nodeData.availabilityClaim;
    return {
      idClaim, availabilityClaim
    };
  }
}
registerNode(new AuthenticatorAvailability());
